/**
 * HeroPromoBanner — banner editable mostrado en el hero.
 * - Solo la píldora CTA es un link (no toda la barra).
 * - El texto "detail" (letra chica) abre un modal con los términos legales si están definidos.
 * - Si no hay promo activa, no renderiza nada (no rompe la estructura del hero).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const isExternalUrl = (url) => /^https?:\/\//i.test(url || '');

function TermsModal({ headline, terms, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div className="promo-modal-bg" onClick={onClose} data-testid="promo-terms-modal">
      <div
        className="promo-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Términos y condiciones de la promoción"
      >
        <header className="promo-modal-head">
          <div>
            <span className="promo-modal-kicker">Términos y condiciones</span>
            <h3>{headline}</h3>
          </div>
          <button
            type="button"
            className="promo-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="promo-terms-close"
          >×</button>
        </header>
        <div className="promo-modal-body">{terms}</div>
        <footer className="promo-modal-foot">
          <button
            type="button"
            className="promo-modal-cta"
            onClick={onClose}
            data-testid="promo-terms-accept"
          >Entendido</button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

export default function HeroPromoBanner() {
  const [promo, setPromo] = useState(null);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/promo/active`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data?.active && data?.headline) setPromo(data);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const closeModal = useCallback(() => setShowTerms(false), []);

  if (!promo) return null;

  const hasLink = !!promo.cta_url;
  const ctaText = promo.cta_label || (hasLink ? 'Ver más' : null);
  const external = isExternalUrl(promo.cta_url);
  const align = ['left', 'center', 'right'].includes(promo.align) ? promo.align : 'left';
  const alignItems = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
  const hasTerms = !!(promo.terms && promo.terms.trim());

  return (
    <>
      <div className="hero-promo-banner" data-testid="hero-promo-banner">
        <span className="hero-promo-spark" aria-hidden>✦</span>
        <span className="hero-promo-text" style={{ alignItems }}>
          {promo.eyebrow ? (
            <span className="hero-promo-eyebrow" style={{ textAlign: align }}>{promo.eyebrow}</span>
          ) : null}
          <strong style={{ textAlign: align }}>{promo.headline}</strong>
          {promo.detail ? (
            hasTerms ? (
              <button
                type="button"
                className="hero-promo-detail is-link"
                style={{ textAlign: align }}
                onClick={() => setShowTerms(true)}
                data-testid="hero-promo-detail-btn"
                aria-haspopup="dialog"
              >
                {promo.detail} <span className="hero-promo-detail-icon" aria-hidden>ⓘ</span>
              </button>
            ) : (
              <span className="hero-promo-detail" style={{ textAlign: align }}>{promo.detail}</span>
            )
          ) : null}
        </span>
        {hasLink && ctaText && (
          <a
            href={promo.cta_url}
            className="hero-promo-cta-pill"
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            data-testid="hero-promo-cta"
          >
            {ctaText}
            <span className="hero-promo-arrow" aria-hidden>→</span>
          </a>
        )}
        {!hasLink && ctaText && (
          <span className="hero-promo-cta-pill">
            {ctaText}
            <span className="hero-promo-arrow" aria-hidden>→</span>
          </span>
        )}
      </div>

      {showTerms && hasTerms && (
        <TermsModal
          headline={promo.headline}
          terms={promo.terms}
          onClose={closeModal}
        />
      )}
    </>
  );
}
